import { NOOP_FN } from "@/constants/stable-empty";
import { createContext, useContext } from "react";
import { JsonType } from "./string-utils";

/**
 * Global state for the app
 */
export interface GlobalAppStateType extends Record<string, JsonType | null | undefined> {
  /**
   * Whether the ProjectNavbar is open or not
   */
  sidebarOpen: boolean;
  /**
   * User's preferred ProjectNavbar open state on routes that do not request collapsed nav.
   */
  sidebarOpenPreference?: boolean;
  /**
   * Whether `sidebarOpen` is closed because the reader is on Settings, which the category nav
   * still shows its sidebar on. Not persisted, like the two above.
   */
  sidebarClosedForSettings?: boolean;
  /**
   * Whether or not we should override permissions UI based on admin status
   */
  adminMode: boolean;
  /**
   * Demo mode: admin-only components render for chalk employees (even with adminMode off),
   * but without the red "chalk admin" visual treatment. Toggled with mod+shift+D.
   */
  demoMode: boolean;
  /**
   * The current site version, if known
   */
  siteVersion: string | undefined;
  /**
   * User's preferred grouping for online queries page
   */
  onlineQueriesPageGroupBy?: "name" | "resource_group";
  /**
   * Whether the user has collapsed warning banners shown at the top of the dashboard.
   */
  warningBannersCollapsed: boolean;
}

export interface GlobalAppStateContextType {
  state: GlobalAppStateType;
  /** A stable function that allows users to update state */
  updateState: (s: Partial<GlobalAppStateType>) => void;
}

export const GlobalAppStateContext = createContext<GlobalAppStateContextType>({
  state: {
    sidebarOpen: true,
    sidebarOpenPreference: true,
    adminMode: true,
    demoMode: false,
    siteVersion: undefined,
    warningBannersCollapsed: false,
  },
  updateState: NOOP_FN,
});

export function useGlobalAppState() {
  return useContext(GlobalAppStateContext);
}
